(() => {
  const VERSION = '1.1';
  const ATTR_KEY = 'recAnalyticsAttributionV1';
  const ADD_KEY = 'recAnalyticsLastAddV1';
  const JOURNEY_KEY = 'recAnalyticsJourneyV1';
  const SESSION_KEY = 'recAnalyticsSessionV1';
  const EVENT_PREFIX = 'rec_storefront';
  const MAX_JOURNEY_STEPS = 30;
  const fitSeen = new WeakSet();

  const privacyAllowsAnalytics = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
      return privacy.analyticsProcessingAllowed() === true;
    } catch (_) {
      return false;
    }
  };

  const pageType = () => {
    const cls = [...(document.body?.classList || [])].find((name) => name.startsWith('template-'));
    return cls ? cls.replace(/^template-/, '') : 'unknown';
  };

  const safePath = (value = window.location.href) => {
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? (url.pathname || '/') : 'external';
    } catch (_) {
      return '';
    }
  };

  const cleanToken = (value, max = 80) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, max);

  const safeSource = (value) => {
    const token = cleanToken(value, 40);
    return token || undefined;
  };

  const readSession = (key) => {
    if (!privacyAllowsAnalytics()) return null;
    try {
      const value = window.sessionStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (_) {
      return null;
    }
  };

  const writeSession = (key, value) => {
    if (!privacyAllowsAnalytics()) return false;
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (_) {
      return false;
    }
  };

  const publish = (eventName, payload = {}) => {
    if (!privacyAllowsAnalytics()) return false;
    const analytics = window.Shopify?.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;
    const data = {
      event_version: VERSION,
      page_path: safePath(),
      page_type: pageType(),
      ...payload
    };
    try {
      const result = analytics.publish(`${EVENT_PREFIX}:${eventName}`, data);
      if (result && typeof result.catch === 'function') result.catch(() => {});
      return true;
    } catch (_) {
      return false;
    }
  };

  const deviceClass = () => {
    try {
      if (window.matchMedia('(max-width: 749px)').matches) return 'mobile';
      if (window.matchMedia('(max-width: 1024px)').matches) return 'tablet';
    } catch (_) {}
    return 'desktop';
  };

  const trafficContext = () => {
    let referrerType = 'direct';
    let referrerHost;
    try {
      if (document.referrer) {
        const ref = new URL(document.referrer);
        if (ref.origin === window.location.origin) {
          referrerType = 'internal';
        } else {
          referrerType = 'external';
          referrerHost = cleanToken(ref.hostname, 64) || undefined;
        }
      }
    } catch (_) {}

    let entrySource;
    let entryMedium;
    let entryCampaign;
    try {
      const params = new URLSearchParams(window.location.search);
      entrySource = safeSource(params.get('utm_source'));
      entryMedium = safeSource(params.get('utm_medium'));
      entryCampaign = cleanToken(params.get('utm_campaign'), 64) || undefined;
    } catch (_) {}

    return {
      referrer_type: referrerType,
      ...(referrerHost ? { referrer_host: referrerHost } : {}),
      ...(entrySource ? { entry_source: entrySource } : {}),
      ...(entryMedium ? { entry_medium: entryMedium } : {}),
      ...(entryCampaign ? { entry_campaign: entryCampaign } : {})
    };
  };

  const ensureSessionState = () => {
    if (!privacyAllowsAnalytics()) return null;
    const existing = readSession(SESSION_KEY);
    if (existing?.started_at) return existing;
    const state = {
      started_at: Date.now(),
      landing_path: safePath(),
      device: deviceClass(),
      ...trafficContext()
    };
    writeSession(SESSION_KEY, state);
    return state;
  };

  const resourceHandle = (kind = pageType()) => {
    const root = kind === 'product' ? 'products' : kind === 'collection' ? 'collections' : '';
    if (!root) return '';
    try {
      const parts = window.location.pathname.split('/').filter(Boolean);
      const index = parts.indexOf(root);
      return index >= 0 ? cleanToken(parts[index + 1], 96) : '';
    } catch (_) {
      return '';
    }
  };

  const recordJourney = (step = {}) => {
    if (!privacyAllowsAnalytics()) return null;
    ensureSessionState();
    const current = readSession(JOURNEY_KEY);
    const journey = Array.isArray(current) ? current : [];
    const entry = {
      kind: cleanToken(step.kind || 'event', 32),
      action: cleanToken(step.action || '', 48),
      page_type: pageType(),
      page_path: safePath(),
      at: Date.now()
    };
    const feature = cleanToken(step.feature || '', 48);
    const lane = safeSource(step.lane);
    const source = safeSource(step.source);
    const handle = cleanToken(step.resource_handle || '', 96);
    if (feature) entry.feature = feature;
    if (lane) entry.lane = lane;
    if (source) entry.source = source;
    if (handle) entry.resource_handle = handle;
    journey.push(entry);
    const capped = journey.slice(-MAX_JOURNEY_STEPS);
    writeSession(JOURNEY_KEY, capped);
    return entry;
  };

  const journeySummary = () => {
    if (!privacyAllowsAnalytics()) return {};
    const state = ensureSessionState() || {};
    const journey = readSession(JOURNEY_KEY);
    const steps = Array.isArray(journey) ? journey : [];
    const featureSteps = steps.filter((step) => step.kind === 'feature' && step.feature);
    const first = featureSteps[0];
    const last = featureSteps[featureSteps.length - 1];
    const productHandles = new Set(steps.filter((step) => step.action === 'product_view' && step.resource_handle).map((step) => step.resource_handle));

    return {
      journey_steps: steps.length,
      session_age_ms: Math.max(0, Date.now() - Number(state.started_at || Date.now())),
      landing_path: state.landing_path || '',
      device_class: state.device || deviceClass(),
      ...(state.referrer_type ? { entry_referrer_type: state.referrer_type } : {}),
      ...(state.referrer_host ? { entry_referrer_host: state.referrer_host } : {}),
      ...(state.entry_source ? { entry_source: state.entry_source } : {}),
      ...(state.entry_medium ? { entry_medium: state.entry_medium } : {}),
      ...(state.entry_campaign ? { entry_campaign: state.entry_campaign } : {}),
      ...(first?.feature ? { first_feature: first.feature } : {}),
      ...(first?.action ? { first_action: first.action } : {}),
      ...(first?.lane ? { first_lane: first.lane } : {}),
      ...(last?.feature ? { last_feature: last.feature } : {}),
      ...(last?.action ? { last_action: last.action } : {}),
      ...(last?.lane ? { last_lane: last.lane } : {}),
      product_views: steps.filter((step) => step.action === 'product_view').length,
      unique_products_viewed: productHandles.size,
      collection_views: steps.filter((step) => step.action === 'collection_view').length,
      search_views: steps.filter((step) => step.action === 'search_view').length,
      cart_views: steps.filter((step) => step.action === 'cart_view').length
    };
  };

  const makeTouch = ({ feature, action, lane, source, destination }) => {
    const touch = {
      feature: cleanToken(feature, 48),
      action: cleanToken(action, 48),
      page_path: safePath(),
      touched_at: Date.now()
    };
    const laneToken = safeSource(lane);
    const sourceToken = safeSource(source);
    const destinationPath = destination ? safePath(destination) : '';
    if (laneToken) touch.lane = laneToken;
    if (sourceToken) touch.source = sourceToken;
    if (destinationPath) touch.destination_path = destinationPath;
    return touch;
  };

  const engage = (details, persist = true) => {
    if (!privacyAllowsAnalytics()) return false;
    ensureSessionState();
    const touch = makeTouch(details);
    if (!touch.feature || !touch.action) return false;
    if (persist) writeSession(ATTR_KEY, touch);
    recordJourney({
      kind: 'feature',
      feature: touch.feature,
      action: touch.action,
      lane: touch.lane,
      source: touch.source
    });
    publish('feature_engaged', {
      ...touch,
      ...journeySummary()
    });
    return true;
  };

  const attributionPayload = (touch) => {
    if (!touch) return null;
    return {
      attributed_feature: touch.feature,
      attributed_action: touch.action,
      ...(touch.lane ? { attributed_lane: touch.lane } : {}),
      ...(touch.source ? { attributed_source: touch.source } : {}),
      ...(touch.destination_path ? { attributed_destination_path: touch.destination_path } : {}),
      attribution_page_path: touch.page_path || '',
      attribution_age_ms: Math.max(0, Date.now() - Number(touch.touched_at || Date.now()))
    };
  };

  const currentRouterLane = () => {
    try { return cleanToken(window.sessionStorage.getItem('recIntentLane'), 32); }
    catch (_) { return ''; }
  };

  const completeBuildSource = (element) => element?.closest?.('[data-rec-complete-build]')?.dataset.recCompleteBuildSource || '';

  const handleFeatureClick = (event) => {
    const target = event.target.closest?.('a,button,input,[role="button"]');
    if (!target) return;

    const destination = target.closest('a[href]')?.href || '';

    if (target.matches('[data-rec-intent]')) {
      engage({ feature: 'router', action: 'lane_select', lane: target.dataset.recIntent });
      return;
    }
    if (target.matches('[data-rec-router-primary]')) {
      engage({ feature: 'router', action: 'primary_click', lane: currentRouterLane(), destination });
      return;
    }
    if (target.closest('[data-rec-router-extras]')) {
      engage({ feature: 'router', action: 'extra_click', lane: currentRouterLane(), destination });
      return;
    }

    if (target.matches('[data-rec-finder-tab]')) {
      engage({ feature: 'finder', action: 'tab_select', lane: target.dataset.recFinderTab });
      return;
    }
    if (target.matches('[data-rec-query]')) {
      engage({ feature: 'finder', action: 'example_search' });
      return;
    }
    if (target.matches('[data-rec-route-link]')) {
      engage({ feature: 'finder', action: 'router_resume', lane: currentRouterLane(), destination });
      return;
    }
    if (target.closest('.rec-finder__pick')) {
      const pick = target.closest('.rec-finder__pick');
      const panel = pick.closest('[data-rec-finder-panel]');
      engage({ feature: 'finder', action: 'destination_click', lane: panel?.dataset.recFinderPanel, destination: pick.href });
      return;
    }

    if (target.closest('[data-rec-psearch-brain]')) {
      engage({ feature: 'search_brain', action: 'suggestion_click', lane: currentRouterLane(), destination });
      return;
    }
    if (target.matches('.rec-psearch__lane--finder')) {
      engage({ feature: 'predictive_search', action: 'open_finder', lane: currentRouterLane(), destination });
      return;
    }
    if (target.matches('.rec-psearch__lane')) {
      engage({ feature: 'predictive_search', action: 'lane_shortcut', lane: currentRouterLane(), destination });
      return;
    }
    if (target.closest('[data-rec-psearch-route]')) {
      engage({ feature: 'predictive_search', action: 'router_resume', lane: currentRouterLane(), destination });
      return;
    }
    if (target.matches('[data-rec-search-action]')) {
      engage({ feature: 'predictive_search', action: 'view_all_results', lane: currentRouterLane(), destination });
      return;
    }

    const buildRoot = target.closest('[data-rec-complete-build]');
    if (buildRoot) {
      const action = target.matches('.rec-complete-build__choose') ? 'choose_options' : 'product_open';
      engage({ feature: 'complete_build', action, source: completeBuildSource(target), destination });
      return;
    }

    if (target.matches('[data-rec-passport-open]')) {
      engage({ feature: 'product_passport', action: 'open' });
      return;
    }
    if (target.matches('[data-rec-passport-standard]')) {
      engage({ feature: 'product_passport', action: 'open_rec_standard' });
      return;
    }
    if (target.closest('.rec-passport__links')) {
      engage({ feature: 'product_passport', action: 'help_click', destination });
      return;
    }

    if (target.matches('.rec-stash__hide')) {
      engage({ feature: 'stash', action: 'hide_for_now' }, false);
      return;
    }
    if (target.closest('.rec-stash .ftf-products')) {
      engage({ feature: 'stash', action: 'product_reopen', destination });
      return;
    }

    if (target.closest('[data-rec-ready-when-product], [data-rec-ready-when-cart]')) {
      engage({ feature: 'ready_when', action: 'help_click', destination }, false);
      return;
    }

    if (target.closest('[data-rec-native-bundle]')) {
      engage({ feature: 'native_bundle', action: 'standalone_piece_click', destination });
      return;
    }

    if (target.matches('[data-rec-account-route]')) {
      engage({ feature: 'my_rec', action: 'account_route', lane: target.dataset.recAccountRoute, destination }, false);
      return;
    }
    if (target.matches('header a[href*="/account"], .ftf-toolbar a[href*="/account"]')) {
      engage({ feature: 'my_rec', action: 'open' }, false);
      return;
    }

    if (target.matches('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]')) {
      recordJourney({ kind: 'conversion', action: 'checkout_start' });
      const summary = journeySummary();
      const lastAdd = readSession(ADD_KEY) || readSession(ATTR_KEY);
      const attribution = attributionPayload(lastAdd);
      publish('checkout_start_journey', summary);
      if (attribution) publish('checkout_start_attributed', { ...attribution, ...summary });
    }
  };

  const handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = form.getAttribute('action') || '';
    if (action.includes('/cart/add')) {
      const buildRoot = form.closest('[data-rec-complete-build]');
      if (buildRoot) {
        engage({ feature: 'complete_build', action: 'quick_add', source: buildRoot.dataset.recCompleteBuildSource });
      }

      recordJourney({
        kind: 'conversion',
        action: 'add_to_cart',
        resource_handle: pageType() === 'product' ? resourceHandle('product') : ''
      });
      const summary = journeySummary();
      const touch = readSession(ATTR_KEY);
      const attribution = attributionPayload(touch);
      if (touch) writeSession(ADD_KEY, touch);

      publish('add_to_cart_journey', {
        add_context: buildRoot ? 'complete_build_quick_add' : 'product_form',
        ...(pageType() === 'product' && resourceHandle('product') ? { product_handle: resourceHandle('product') } : {}),
        ...summary
      });

      if (attribution) {
        publish('add_to_cart_attributed', {
          ...attribution,
          add_context: buildRoot ? 'complete_build_quick_add' : 'product_form',
          ...summary
        });
      }
      return;
    }

    const q = form.querySelector('input[name="q"]');
    if (q) {
      engage({
        feature: form.closest('#ftf-search-hidden') ? 'predictive_search' : 'search',
        action: 'submit',
        lane: currentRouterLane()
      });
    }
  };

  const handleChange = (event) => {
    const input = event.target.closest?.('[data-rec-fit-width]');
    if (!input || fitSeen.has(input) || !String(input.value || '').trim()) return;
    fitSeen.add(input);
    engage({ feature: 'product_passport', action: 'fit_check_used' });
  };

  const trackPageMilestone = () => {
    const type = pageType();
    const actionByType = {
      index: 'home_view',
      product: 'product_view',
      collection: 'collection_view',
      search: 'search_view',
      cart: 'cart_view'
    };
    const action = actionByType[type];
    if (!action) return;

    const handle = resourceHandle(type);
    recordJourney({ kind: 'page', action, resource_handle: handle });
    publish('journey_milestone', {
      milestone: action,
      ...(handle ? { resource_handle: handle } : {}),
      ...journeySummary()
    });
  };

  const clearAttribution = () => {
    try {
      window.sessionStorage.removeItem(ATTR_KEY);
      window.sessionStorage.removeItem(ADD_KEY);
      window.sessionStorage.removeItem(JOURNEY_KEY);
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch (_) {}
  };

  const boot = () => {
    if (!privacyAllowsAnalytics()) clearAttribution();
    else {
      ensureSessionState();
      trackPageMilestone();
    }

    document.addEventListener('visitorConsentCollected', () => {
      if (!privacyAllowsAnalytics()) {
        clearAttribution();
        return;
      }
      ensureSessionState();
    });
    document.addEventListener('click', handleFeatureClick, true);
    document.addEventListener('submit', handleSubmit, true);
    document.addEventListener('change', handleChange, true);
  };

  window.RECAnalytics = Object.freeze({
    version: VERSION,
    analyticsAllowed: privacyAllowsAnalytics,
    publish,
    engage,
    getAttribution: () => readSession(ATTR_KEY),
    getJourney: () => readSession(JOURNEY_KEY) || [],
    getJourneySummary: journeySummary,
    snapshot: () => ({
      attribution: readSession(ATTR_KEY),
      last_add: readSession(ADD_KEY),
      session: readSession(SESSION_KEY),
      journey: readSession(JOURNEY_KEY) || [],
      summary: journeySummary()
    })
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
